import React, { createContext, useContext, useState, useCallback } from 'react';
import Toast, { ToastProps } from './Toast';

interface ToastMessage extends Omit<ToastProps, 'onClose'> {
    id: string;
}

// Toast上下文的类型定义
interface ToastContextType {
    showToast: (message: string, type?: ToastProps['type'], duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// 自定义Hook：用于在任何组件中调用toast方法
export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

// ToastProvider组件的属性类型
interface ToastProviderProps {
    children: React.ReactNode; // 子组件
}

// ToastProvider组件：提供toast功能的上下文提供者
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
    // 存储当前显示的所有toast消息
    const [toasts, setToasts] = useState<ToastMessage[]>([]);

    const showToast = useCallback((
        message: string,
        type: ToastProps['type'] = 'success',
        duration?: number
    ) => {
        const id = Date.now().toString();

        // 根据消息类型设置不同的默认显示时间
        let defaultDuration = duration;
        if (defaultDuration === undefined) {
            switch (type) {
                case 'success':
                    defaultDuration = 3000;
                    break;
                case 'info':
                    defaultDuration = 4000;
                    break;
                case 'warning':
                    defaultDuration = 5000;
                    break;
                case 'error':
                    defaultDuration = 6000; // 错误消息显示更久
                    break;
                default:
                    defaultDuration = 3000;
            }
        }

        const newToast: ToastMessage = {
            id,
            message,
            type,
            duration: defaultDuration,
        };

        // 将新toast添加到现有toast列表中
        setToasts(prev => [...prev, newToast]);
    }, []); // 空依赖数组，函数内部没有依赖外部变量

    // 移除指定ID的toast
    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
                {toasts.map((toast) => (
                    <Toast
                        key={toast.id}
                        message={toast.message}
                        type={toast.type}
                        duration={toast.duration} // 显示持续时间
                        onClose={() => removeToast(toast.id)}
                    />
                ))}
            </div>
        </ToastContext.Provider>
    );
};